import { useState } from 'react';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';
import { useApi } from 'utils/request/useApi';

/**
 * Body for `POST /aim/flow-path-lookups/:snapshotId` — exactly one of
 * `applicationId` (Applications tab) or `flowId` (Flows tab), plus an optional
 * internal-relative `callbackUrl` the completion toast links to.
 */
export type AimFlowPathLookupBody =
  | { applicationId: string; callbackUrl?: string }
  | { flowId: string; callbackUrl?: string };

/**
 * Schedules the AIM flow path-lookup job for one application or flow.
 *
 * The job runs asynchronously; completion is surfaced via the header job
 * notification (which links to `callbackUrl` when present), so this only fires
 * the request and confirms scheduling — it does not reload the table.
 *
 * Shared by the two presentations of the trigger: the refresh icon button in the
 * Applications `Flows calculated` cell, and the `Calculate` link in the Flows
 * `Calculation status` cell.
 */
export const useCalculateDevices = (
  body: AimFlowPathLookupBody,
): {
  calculate: () => Promise<void>;
  scheduling: boolean;
  disabled: boolean;
} => {
  const api = useApi();
  const snapshotId = useSnapshotId();
  const { showNotification } = useNotifications();
  const [scheduling, setScheduling] = useState(false);

  const calculate = async () => {
    if (!snapshotId) {
      return;
    }
    setScheduling(true);
    try {
      await api.request({
        url: `/aim/flow-path-lookups/${encodeURIComponent(snapshotId)}`,
        method: 'POST',
        data: body,
      });
      showNotification({
        type: 'success',
        message: 'Device path-lookup calculation scheduled',
      });
    } catch {
      showNotification({
        type: 'danger',
        message: 'Failed to schedule device path-lookup calculation',
      });
    } finally {
      setScheduling(false);
    }
  };

  return { calculate, scheduling, disabled: !snapshotId };
};
